import { useNavigation } from "@react-navigation/native";
import {
  Text,
  TopNavigation,
  TopNavigationAction,
} from "@ui-kitten/components";
import { TouchableWebElement } from "@ui-kitten/components/devsupport";
import Chevron from "components/Chevron";
import InnerLoading from "components/InnerLoading";
import { useAppSelector } from "hooks/useAppSelector";
import { SeatSelectionScreenProps } from "navigation/types";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { useFetchInventoryTypesQuery } from "queries/ticket.queries";
import { useMemo, useState } from "react";
import { View, useWindowDimensions } from "react-native";
import {
  NavigationState,
  SceneMap,
  SceneRendererProps,
  TabBar,
  TabView,
} from "react-native-tab-view";
import { formatEventDate } from "utils/eventUtils";
import PremiumSeatSelectionFlow from "./PremiumSeatSelectionFlow";
import StandardSeatSelectionFlow from "./StandardSeatSelectionFlow";

type RenderTabBarType = SceneRendererProps & {
  navigationState: NavigationState<{
    key: string;
    title: string;
  }>;
};

const renderScene = SceneMap({
  premiumFlow: PremiumSeatSelectionFlow,
  standardFlow: StandardSeatSelectionFlow,
});

const SeatSelection: React.FC<SeatSelectionScreenProps> = () => {
  const { data: branding } = useFetchTenantBrandingQuery();
  const { selectedEvent, selectedSubscription, isChangingSeats } =
    useAppSelector((state) => state.reservationFlow);
  const navigation = useNavigation();
  const { data: inventoryTypes } = useFetchInventoryTypesQuery({
    planPackageId: selectedSubscription?.planPackage.id,
    eventId: selectedEvent?.eventId,
  });

  const layout = useWindowDimensions();
  const [index, setIndex] = useState(0);

  const routes = useMemo(() => {
    const result = [];
    if (inventoryTypes?.containsStandard) {
      result.push({ key: "standardFlow", title: "Standard Seating" });
    }
    if (inventoryTypes?.containsPremium) {
      result.push({ key: "premiumFlow", title: "Premium Experiences" });
    }
    return result;
  }, [inventoryTypes]);

  const renderTabBar = (props: RenderTabBarType) => (
    <TabBar
      {...props}
      indicatorStyle={{ backgroundColor: "black" }}
      style={{ backgroundColor: "transparent" }}
      renderLabel={({ route }) => (
        <Text style={{ color: "black" }}>{route.title}</Text>
      )}
      tabStyle={{ backgroundColor: "transparent", minHeight: 30 }}
    />
  );
  const renderBackAction = (): TouchableWebElement => (
    <TopNavigationAction
      onPress={navigation.goBack}
      icon={<Chevron direction="left" color="white" />}
    />
  );

  if (!inventoryTypes) return <InnerLoading />;
  return (
    <View style={{ flex: 1 }}>
      <TopNavigation
        style={{ backgroundColor: branding?.primaryColor }}
        alignment="center"
        title={() => (
          <Text category="h6" status="control" numberOfLines={1}>
            {isChangingSeats ? "Updating Reservation: " : ""}
            {selectedEvent!.eventName}
          </Text>
        )}
        subtitle={() => (
          <Text category="c2" status="control">
            {formatEventDate(selectedEvent!)}
          </Text>
        )}
        accessoryLeft={renderBackAction}
      />
      {/* has both inventory types */}
      {routes.length > 1 ? (
        <TabView
          navigationState={{ index, routes }}
          renderScene={renderScene}
          onIndexChange={setIndex}
          initialLayout={{ width: layout.width }}
          renderTabBar={renderTabBar}
          swipeEnabled={false}
        />
      ) : inventoryTypes.containsPremium ? (
        <PremiumSeatSelectionFlow />
      ) : (
        <StandardSeatSelectionFlow />
      )}
    </View>
  );
};

export default SeatSelection;
